import { useEffect, useState, useCallback } from 'react';
import { blogDb, BLOG_ID } from '@/integrations/supabase/client';

export interface Video {
  id: string;
  youtube_id: string;
  title: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

// bloggeral.video_carousel stores full URLs; the app works with the 11-char YouTube id.
const ytUrl = (id: string) => `https://www.youtube.com/watch?v=${id}`;
const ytThumb = (id: string) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
const rowToVideo = (r: any): Video => ({
  id: r.id,
  youtube_id: extractYouTubeId(r.video_url) || '',
  title: r.title,
  display_order: r.sort_order ?? 0,
  is_active: r.is_active,
  created_at: r.created_at,
});

/** Public hook — only active videos, ordered. */
export const useActiveVideos = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error } = await blogDb
        .from('video_carousel')
        .select('*')
        .eq('blog_id', BLOG_ID)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (!mounted) return;
      if (!error && data) setVideos(data.map(rowToVideo));
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  return { videos, loading };
};

/** Admin hook — all videos + CRUD. */
export const useAdminVideos = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await blogDb
      .from('video_carousel')
      .select('*')
      .eq('blog_id', BLOG_ID)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (!error && data) setVideos(data.map(rowToVideo));
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addVideo = useCallback(async (youtube_id: string, title: string) => {
    const maxOrder = videos.reduce((m, v) => Math.max(m, v.display_order), -1);
    const { error } = await blogDb
      .from('video_carousel')
      .insert({
        blog_id: BLOG_ID,
        video_url: ytUrl(youtube_id),
        thumbnail_url: ytThumb(youtube_id),
        title: title || null,
        sort_order: maxOrder + 1,
        is_active: true,
      });
    if (error) throw error;
    await refresh();
  }, [videos, refresh]);

  const updateVideo = useCallback(async (id: string, patch: Partial<Pick<Video, 'youtube_id' | 'title' | 'is_active' | 'display_order'>>) => {
    const db: Record<string, any> = {};
    if (patch.youtube_id !== undefined) { db.video_url = ytUrl(patch.youtube_id); db.thumbnail_url = ytThumb(patch.youtube_id); }
    if (patch.title !== undefined) db.title = patch.title;
    if (patch.is_active !== undefined) db.is_active = patch.is_active;
    if (patch.display_order !== undefined) db.sort_order = patch.display_order;
    const { error } = await blogDb.from('video_carousel').update(db).eq('id', id);
    if (error) throw error;
    await refresh();
  }, [refresh]);

  const deleteVideo = useCallback(async (id: string) => {
    const { error } = await blogDb.from('video_carousel').delete().eq('id', id);
    if (error) throw error;
    await refresh();
  }, [refresh]);

  const moveVideo = useCallback(async (id: string, direction: 'up' | 'down') => {
    const idx = videos.findIndex(v => v.id === id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= videos.length) return;
    const a = videos[idx];
    const b = videos[swapIdx];
    await Promise.all([
      blogDb.from('video_carousel').update({ sort_order: b.display_order }).eq('id', a.id),
      blogDb.from('video_carousel').update({ sort_order: a.display_order }).eq('id', b.id),
    ]);
    await refresh();
  }, [videos, refresh]);

  return { videos, loading, addVideo, updateVideo, deleteVideo, moveVideo, refresh };
};

/** Extracts the YouTube video id from a URL or returns the input if it already looks like an id. */
export const extractYouTubeId = (input: string): string | null => {
  if (!input) return null;
  const trimmed = input.trim();
  // Pure ID (11 chars, alphanumeric + - _)
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  // Try URL parsing
  try {
    const url = new URL(trimmed);
    // youtu.be/ID
    if (url.hostname.includes('youtu.be')) {
      const id = url.pathname.slice(1).split('/')[0];
      if (/^[a-zA-Z0-9_-]{11}$/.test(id)) return id;
    }
    // youtube.com/watch?v=ID
    const v = url.searchParams.get('v');
    if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
    // youtube.com/shorts/ID  or  /embed/ID
    const parts = url.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && /^[a-zA-Z0-9_-]{11}$/.test(last)) return last;
  } catch { /* not a URL */ }
  return null;
};
